<!-- TEMPLATE CONTRACT: Ui Table Component owns the visible Angular template structure for src/app/shared/ui/ui-table/ui-table.component.html. -->
<section class="ui-table-block" [class.ui-table-block--paginated]="showPagination()">
  <!-- Conditional branch documents the visible UI state for this condition. -->
  @if (shouldShowToolbar()) {
    <header class="ui-table-toolbar">
      <!-- Conditional branch documents the visible UI state for this condition. -->
      @if (showSearch()) {
        <div class="ui-table-toolbar__search-row">
          <span class="ui-table-toolbar__search-label">{{ 'Search' | uiLabelTranslate }}</span>
          <div class="ui-table-toolbar__search-wrap">
            <span class="ui-table-toolbar__search-icon material-symbols-rounded" aria-hidden="true">search</span>
            <ui-form-field
              class="ui-table-toolbar__search"
              label=""
              type="search"
              [placeholder]="searchPlaceholder()"
              [value]="searchQuery()"
              (valueChange)="onSearchChange($event)"
            ></ui-form-field>
          </div>
        </div>
      }

      <div class="ui-table-toolbar__controls">
        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (showSelection() && showBulkDeleteButton()) {
          <ui-button variant="danger" [disabled]="selectedRows().length === 0" (click)="triggerBulkDelete()">
            {{ bulkDeleteLabel() }}
          </ui-button>
        }

        <!-- Toolbar/filter section for narrowing visible data. -->
        @if (showFilter() && filterFields().length > 0) {
          <ui-button variant="secondary" (click)="openFilterPanel()">
            <span class="ui-table-toolbar__filter-button">
              <span class="material-symbols-rounded" aria-hidden="true">filter_list</span>
              <span class="ui-table-toolbar__filter-label">{{ 'Filters' | uiLabelTranslate }}</span>
              <span class="ui-table-toolbar__filter-count">{{ appliedFilterCount() }}</span>
            </span>
          </ui-button>
        }
      </div>
    </header>
  }

  <!-- Conditional branch documents the visible UI state for this condition. -->
  @if (!hasVisibleRows()) {
    <div class="ui-table__empty-state-container">
      <ui-empty-state [title]="emptyMessage()" [description]="''"></ui-empty-state>
    </div>
  } @else {
  <p-table
    class="ui-table-wrap"
    [value]="pagedRows()"
    [columns]="columns()"
    [lazy]="false"
    [scrollable]="true"
    scrollHeight="flex"
    [globalFilterFields]="searchKeys()"
    responsiveLayout="scroll"
    [attr.aria-label]="ariaLabel() | uiLabelTranslate"
  >
    <ng-template pTemplate="header">
      <tr>
        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (showSelection()) {
          <th class="p-datatable-checkbox-cell">
            <input
              type="checkbox"
              [checked]="allDisplayedSelected()"
              (change)="toggleAllDisplayed(masterCheck.checked)"
              #masterCheck
            />
          </th>
        }

        <!-- Repeated block documents how collection data renders in the UI. -->
        @for (column of columns(); track column.key) {
          <th
            [style.width]="column.width || null"
            [style.min-width]="column.minWidth || null"
            [style.max-width]="column.maxWidth || null"
            [style.white-space]="column.whiteSpace || null"
            [style.text-align]="column.align || null"
          >
            {{ column.label | uiLabelTranslate }}
          </th>
        }

        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (showActionsColumn()) {
          <th class="ui-table__actions-column">{{ actionsColumnLabel() | uiLabelTranslate }}</th>
        }
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-row>
      <tr>
        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (showSelection()) {
          <td class="p-datatable-checkbox-cell">
            <input
              type="checkbox"
              [checked]="isRowSelected(row)"
              (change)="toggleRowSelection($event, row, rowCheck.checked)"
              #rowCheck
            />
          </td>
        }

        <!-- Repeated block documents how collection data renders in the UI. -->
        @for (column of columns(); track column.key) {
          <td
            [class.ui-table__cell--first-hover]="shouldShowFirstColumnHover(column)"
            [class.ui-table__cell--first-clickable]="shouldShowFirstColumnHover(column) && isFirstColumnInteractive(column)"
            [style.min-width]="column.minWidth || null"
            [style.max-width]="column.maxWidth || null"
            [style.white-space]="column.whiteSpace || null"
            [style.text-align]="column.align || null"
            (click)="onDataCellClick($event, column, row)"
          >
            <!-- Conditional branch documents the visible UI state for this condition. -->
            @if (column.type === 'actions' && column.actions) {
              <div
                class="ui-table__actions"
                [class.ui-table__actions--compact-two]="isCompactTwoActionLayout(column.actions)"
              >
                <!-- Repeated block documents how collection data renders in the UI. -->
                @for (action of visibleActions(column.actions); track action.id) {
                  <ui-button
                    [variant]="action.variant || 'ghost'"
                    size="sm"
                    [ariaLabel]="action.label | uiLabelTranslate"
                    [class.ui-table__icon-action]="isIconOnlyAction(action)"
                    [disabled]="isActionDisabled(action, row)"
                    (click)="onActionClick($event, action.id, row)"
                  >
                    <!-- Conditional branch documents the visible UI state for this condition. -->
                    @if (isIconOnlyAction(action)) {
                      <span class="ui-table__action-icon material-symbols-rounded" aria-hidden="true">{{ actionIconSymbol(action) }}</span>
                    } @else {
                      {{ action.label | uiLabelTranslate }}
                    }
                  </ui-button>
                }
                <!-- Conditional branch documents the visible UI state for this condition. -->
                @if (hasOverflowActions(column.actions)) {
                  <div class="ui-table__overflow-dropdown">
                    <ui-button
                      variant="ghost"
                      size="sm"
                      class="ui-table__icon-action"
                      ariaLabel="shared.table.moreActions"
                      [disabled]="isOverflowTriggerDisabled(overflowActions(column.actions), row)"
                      (click)="toggleDropdown($event, row, overflowActions(column.actions))"
                    >
                      <span class="ui-table__action-icon material-symbols-rounded" aria-hidden="true">more_vert</span>
                    </ui-button>
                    <!-- Conditional branch documents the visible UI state for this condition. -->
                    @if (isDropdownOpen(row)) {
                      <div class="ui-table__dropdown-menu">
                        <!-- Repeated block documents how collection data renders in the UI. -->
                        @for (action of overflowActions(column.actions); track action.id) {
                          <button
                            type="button"
                            class="ui-table__dropdown-item"
                            [class.ui-table__dropdown-item--danger]="action.variant === 'danger'"
                            [disabled]="isActionDisabled(action, row)"
                            (click)="onDropdownActionClick($event, action.id, row)"
                          >
                            <!-- Conditional branch documents the visible UI state for this condition. -->
                            @if (actionIconSymbol(action)) {
                              <span class="material-symbols-rounded" aria-hidden="true">{{ actionIconSymbol(action) }}</span>
                            }
                            {{ action.label | uiLabelTranslate }}
                          </button>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            } @else if (column.type === 'status') {
              <span
                class="ui-table__badge"
                [attr.data-tone]="badgeToneFor(textOf(row, column))"
                [style.--ui-badge-bg]="badgeBgFor(textOf(row, column))"
                [style.--ui-badge-text]="badgeTextFor(textOf(row, column))"
              >
                {{ textOf(row, column) | uiLabelTranslate }}
              </span>
            } @else if (column.type === 'stock') {
              <span
                class="ui-table__badge"
                [attr.data-tone]="badgeToneFor(textOf(row, column))"
                [style.--ui-badge-bg]="badgeBgFor(textOf(row, column))"
                [style.--ui-badge-text]="badgeTextFor(textOf(row, column))"
              >
                {{ textOf(row, column) | uiLabelTranslate }}
              </span>
            } @else if (column.type === 'toggle') {
              <ui-toggle
                [checked]="isToggleChecked(column, row)"
                [disabled]="isToggleDisabled(column, row)"
                [ariaLabel]="toggleAriaLabel(column, row)"
                (toggled)="onToggleChange(column, row, $event)"
              ></ui-toggle>
            } @else if (column.type === 'checkbox') {
              <label class="ui-table__checkbox-cell" [attr.aria-label]="toggleAriaLabel(column, row)">
                <input
                  type="checkbox"
                  class="ui-table__checkbox"
                  [checked]="isToggleChecked(column, row)"
                  [disabled]="isToggleDisabled(column, row)"
                  (change)="onToggleChange(column, row, $any($event.target).checked)"
                  (click)="$event.stopPropagation()"
                />
              </label>
            } @else if (column.type === 'select') {
              <div class="ui-table__select-cell">
                <ui-select
                  [label]="''"
                  [required]="true"
                  [value]="selectValue(column, row)"
                  [options]="selectOptions(column, row)"
                  [disabled]="isSelectDisabled(column, row)"
                  (valueChange)="onSelectChange(column, row, $event)"
                  (click)="$event.stopPropagation()"
                ></ui-select>
              </div>
            } @else if (column.type === 'resource-summary') {
              <ui-resource-summary [items]="resourceSummaryItemsOf(row, column)"></ui-resource-summary>
            } @else if (column.clickable) {
              <button type="button" class="ui-table__link" (click)="onCellClick($event, column, row)">
                {{ textOf(row, column) | uiLabelTranslate }}
              </button>
            } @else {
              {{ textOf(row, column) | uiLabelTranslate }}
            }
          </td>
        }

        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (showActionsColumn()) {
          <td class="ui-table__actions-column">
            <div
              class="ui-table__actions"
              [class.ui-table__actions--compact-two]="isCompactTwoActionLayout(rowActions())"
            >
              <!-- Repeated block documents how collection data renders in the UI. -->
              @for (action of visibleActions(rowActions()); track action.id) {
                <ui-button
                  [variant]="action.variant || 'ghost'"
                  size="sm"
                  [ariaLabel]="action.label | uiLabelTranslate"
                  [class.ui-table__icon-action]="isIconOnlyAction(action)"
                  [disabled]="isActionDisabled(action, row)"
                  (click)="onActionClick($event, action.id, row)"
                >
                  <!-- Conditional branch documents the visible UI state for this condition. -->
                  @if (isIconOnlyAction(action)) {
                    <span class="ui-table__action-icon material-symbols-rounded" aria-hidden="true">{{ actionIconSymbol(action) }}</span>
                  } @else {
                    {{ action.label | uiLabelTranslate }}
                  }
                </ui-button>
              }
              <!-- Conditional branch documents the visible UI state for this condition. -->
              @if (hasOverflowActions(rowActions())) {
                <div class="ui-table__overflow-dropdown">
                  <ui-button
                    variant="ghost"
                    size="sm"
                    class="ui-table__icon-action"
                    ariaLabel="shared.table.moreActions"
                    [disabled]="isOverflowTriggerDisabled(overflowActions(rowActions()), row)"
                    (click)="toggleDropdown($event, row, overflowActions(rowActions()))"
                  >
                    <span class="ui-table__action-icon material-symbols-rounded" aria-hidden="true">more_vert</span>
                  </ui-button>
                  <!-- Conditional branch documents the visible UI state for this condition. -->
                  @if (isDropdownOpen(row)) {
                    <div class="ui-table__dropdown-menu">
                      <!-- Repeated block documents how collection data renders in the UI. -->
                      @for (action of overflowActions(rowActions()); track action.id) {
                        <button
                          type="button"
                          class="ui-table__dropdown-item"
                          [class.ui-table__dropdown-item--danger]="action.variant === 'danger'"
                          [disabled]="isActionDisabled(action, row)"
                          (click)="onDropdownActionClick($event, action.id, row)"
                        >
                          <!-- Conditional branch documents the visible UI state for this condition. -->
                          @if (actionIconSymbol(action)) {
                            <span class="material-symbols-rounded" aria-hidden="true">{{ actionIconSymbol(action) }}</span>
                          }
                          {{ action.label | uiLabelTranslate }}
                        </button>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </td>
        }
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="columns().length + (showSelection() ? 1 : 0) + (showActionsColumn() ? 1 : 0)">
          <div class="ui-table__empty-state-wrapper">
            <ui-empty-state
              [title]="emptyMessage()"
              [description]="''"
            ></ui-empty-state>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
  }

  <!-- Conditional branch documents the visible UI state for this condition. -->
  @if (showPagination() && hasVisibleRows()) {
    <footer class="ui-table-pagination">
      <span class="ui-table-pagination__meta ui-table-pagination__chip">
        {{ 'Showing' | uiLabelTranslate }} {{ pageMeta().start }}-{{ pageMeta().end }} {{ 'of' | uiLabelTranslate }} {{ pageMeta().total }}
      </span>

      <div class="ui-table-pagination__controls">
        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (showPageSizeSelector()) {
          <div class="ui-table-pagination__size-group" [attr.aria-label]="'Rows per page' | uiLabelTranslate">
            <span class="ui-table-pagination__size-label">{{ 'Rows per page' | uiLabelTranslate }}</span>
            <select
              class="ui-table-pagination__size"
              [value]="pageSizeValue()"
              [attr.aria-label]="'Rows per page' | uiLabelTranslate"
              (change)="onPageSizeSelectChange($event)"
            >
              <!-- Repeated block documents how collection data renders in the UI. -->
              @for (pageSizeOption of pageSizeSelectorOptions(); track pageSizeOption) {
                <option [value]="pageSizeOption">{{ pageSizeOption }}</option>
              }
            </select>
          </div>
        }

        <ui-button class="ui-table-pagination__nav-button" variant="secondary" [disabled]="!canGoPrevious()" (click)="previousPage()">{{ 'Previous' | uiLabelTranslate }}</ui-button>
        <ui-button class="ui-table-pagination__nav-button" variant="secondary" [disabled]="!canGoNext()" (click)="nextPage()">{{ 'Next' | uiLabelTranslate }}</ui-button>
      </div>
    </footer>
  }

  <!-- Drawer/detail section for record inspection. -->
  <p-drawer
    [visible]="isFilterDrawerOpen()"
    (visibleChange)="!$event && closeFilterPanel()"
    position="right"
    [modal]="false"
    [header]="'Filter Drawer' | uiLabelTranslate"
    [style]="{ width: '30vw' }"
  >
    <!-- Drawer/detail section for record inspection. -->
    <div class="ui-table-drawer__content">
      <!-- Toolbar/filter section for narrowing visible data. -->
      @for (field of filterFields(); track field.key) {
        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (field.type === 'text') {
          <ui-form-field
            [label]="field.label | uiLabelTranslate"
            [placeholder]="(field.placeholder || '') | uiLabelTranslate"
            [value]="asText(filterValue(field.key))"
            (valueChange)="setFilterDraftText(field.key, $event)"
          ></ui-form-field>
        }

        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (field.type === 'number') {
          <ui-form-field
            [label]="field.label | uiLabelTranslate"
            type="number"
            [placeholder]="(field.placeholder || '') | uiLabelTranslate"
            [value]="asText(filterValue(field.key))"
            (valueChange)="setFilterDraftText(field.key, $event)"
          ></ui-form-field>
        }

        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (field.type === 'date') {
          <ui-datepicker
            [label]="field.label | uiLabelTranslate"
            [placeholder]="(field.placeholder || 'Select date') | uiLabelTranslate"
            [value]="asText(filterValue(field.key))"
            [disabled]="isFilterFieldDisabled(field)"
            (valueChange)="setFilterDraftText(field.key, $event)"
          ></ui-datepicker>
        }

        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (field.type === 'select') {
          <ui-select
            [label]="field.label | uiLabelTranslate"
            [placeholder]="(field.placeholder || 'Select option') | uiLabelTranslate"
            [searchable]="field.searchable === true"
            [searchPlaceholder]="(field.searchPlaceholder || 'Search') | uiLabelTranslate"
            [value]="asText(filterValue(field.key))"
            [options]="filterOptions(field)"
            [disabled]="isFilterFieldDisabled(field)"
            [clearable]="true"
            [loadMoreOnScroll]="field.loadMoreOnScroll === true"
            [loadingMoreOptions]="field.loadingMoreOptions === true"
            [hasMoreOptions]="field.hasMoreOptions === true"
            [virtualScrollItemSize]="field.virtualScrollItemSize || 40"
            (valueChange)="setFilterDraftText(field.key, $event)"
            (loadMoreOptions)="loadMoreFilterOptions(field)"
            (searchChange)="onFilterSearchChange(field, $event)"
          ></ui-select>
        }

        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (field.type === 'multiselect') {
          <ui-select
            [label]="field.label | uiLabelTranslate"
            [placeholder]="(field.placeholder || 'Select option') | uiLabelTranslate"
            [searchable]="field.searchable === true"
            [searchPlaceholder]="(field.searchPlaceholder || 'Search') | uiLabelTranslate"
            [multiple]="true"
            [values]="filterValues(field.key)"
            [options]="filterOptions(field)"
            [disabled]="isFilterFieldDisabled(field)"
            [clearable]="true"
            [loadMoreOnScroll]="field.loadMoreOnScroll === true"
            [loadingMoreOptions]="field.loadingMoreOptions === true"
            [hasMoreOptions]="field.hasMoreOptions === true"
            [virtualScrollItemSize]="field.virtualScrollItemSize || 40"
            (valuesChange)="setFilterDraftValues(field.key, $event)"
            (loadMoreOptions)="loadMoreFilterOptions(field)"
            (searchChange)="onFilterSearchChange(field, $event)"
          ></ui-select>
        }

        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (field.type === 'autocomplete') {
          <ui-autocomplete-field
            [label]="field.label | uiLabelTranslate"
            [placeholder]="(field.placeholder || 'Search option') | uiLabelTranslate"
            [value]="asText(filterValue(field.key))"
            [options]="filterAutocompleteOptions(field)"
            [disabled]="isFilterFieldDisabled(field)"
            (valueChange)="setFilterDraftText(field.key, $event)"
            (optionSelected)="setFilterDraftText(field.key, $event)"
          ></ui-autocomplete-field>
        }

        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (field.type === 'radio') {
          <!-- Drawer/detail section for record inspection. -->
          <fieldset class="ui-table-drawer__field-group">
            <legend>{{ field.label | uiLabelTranslate }}</legend>

            <!-- Drawer/detail section for record inspection. -->
            <div class="ui-table-drawer__radio-list">
              <!-- Repeated block documents how collection data renders in the UI. -->
              @for (option of field.options || []; track option.value) {
                <!-- Drawer/detail section for record inspection. -->
                <label class="ui-table-drawer__radio-option">
                  <input
                    type="radio"
                    [name]="'table-filter-' + field.key"
                    [checked]="filterValue(field.key) === option.value"
                    (change)="setFilterDraftText(field.key, option.value)"
                  />
                  <span>{{ option.label | uiLabelTranslate }}</span>
                </label>
              }
            </div>
          </fieldset>
        }

        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (field.type === 'toggle') {
          <!-- Drawer/detail section for record inspection. -->
          <div class="ui-table-drawer__toggle-field">
            <div>
              <!-- Drawer/detail section for record inspection. -->
              <span class="ui-table-drawer__field-label">{{ field.label | uiLabelTranslate }}</span>
              <!-- Conditional branch documents the visible UI state for this condition. -->
              @if (field.placeholder) {
                <p>{{ field.placeholder | uiLabelTranslate }}</p>
              }
            </div>

            <ui-toggle
              [checked]="filterValue(field.key) === true"
              [ariaLabel]="field.label | uiLabelTranslate"
              (toggled)="setFilterDraftToggle(field.key, $event)"
            ></ui-toggle>
          </div>
        }
      }
    </div>

    <ng-template pTemplate="footer">
      <!-- Drawer/detail section for record inspection. -->
      <footer class="ui-table-drawer__footer ui-table-drawer__footer--split">
        <ui-button variant="secondary" (click)="resetFilters()">{{ 'Reset' | uiLabelTranslate }}</ui-button>
        <ui-button (click)="applyFilters()">{{ 'Apply Filters' | uiLabelTranslate }}</ui-button>
      </footer>
    </ng-template>
  <!-- Drawer/detail section for record inspection. -->
  </p-drawer>

  <!-- Drawer/detail section for record inspection. -->
  <p-drawer
    [visible]="isDetailDrawerOpen()"
    (visibleChange)="!$event && closeDetailPanel()"
    position="right"
    [modal]="true"
    [header]="selectedRowTitle() || ('Record Detail' | uiLabelTranslate)"
    [style]="{ width: '50vw' }"
  >
    <!-- Conditional branch documents the visible UI state for this condition. -->
    @if (activeDetailTab(); as tab) {
      <!-- Conditional branch documents the visible UI state for this condition. -->
      @if (resolvedDetailTabs().length > 0) {
        <!-- Drawer/detail section for record inspection. -->
        <nav class="ui-table-drawer__tabs" [attr.aria-label]="'Detail sections' | uiLabelTranslate">
          <!-- Repeated block documents how collection data renders in the UI. -->
          @for (detailTab of resolvedDetailTabs(); track detailTab.id) {
            <button
              type="button"
              class="ui-table-drawer__tab"
              [class.ui-table-drawer__tab--active]="detailTab.id === tab.id"
              (click)="selectDetailTab(detailTab.id)"
            >
              {{ normalizeDrawerLabel(detailTab.label) | uiLabelTranslate }}
            </button>
          }
        </nav>
      }

      <!-- Drawer/detail section for record inspection. -->
      <section class="ui-table-drawer__content">
        <!-- Conditional branch documents the visible UI state for this condition. -->
        @if (selectedRow() && detailSectionsFor(tab).length > 0) {
          <!-- Repeated block documents how collection data renders in the UI. -->
          @for (section of detailSectionsFor(tab); track section.id) {
            <article class="ui-table-detail-section">
              <!-- Conditional branch documents the visible UI state for this condition. -->
              @if (section.title || section.description) {
                <header class="ui-table-detail-section__header">
                  <!-- Conditional branch documents the visible UI state for this condition. -->
                  @if (section.title) {
                    <h4>{{ section.title | uiLabelTranslate }}</h4>
                  }

                  <!-- Conditional branch documents the visible UI state for this condition. -->
                  @if (section.description) {
                    <p>{{ section.description | uiLabelTranslate }}</p>
                  }
                </header>
              }

              <div class="ui-table-detail-section__fields">
                <!-- Repeated block documents how collection data renders in the UI. -->
                @for (field of section.fields; track field.key) {
                  <div class="ui-table-detail-field">
                    <span class="ui-table-detail-field__label">{{ field.label | uiLabelTranslate }}</span>

                    <!-- Conditional branch documents the visible UI state for this condition. -->
                    @if (field.type === 'status') {
                      <span
                        class="ui-table__badge"
                        [attr.data-tone]="badgeToneFor(detailTextOf(selectedRow(), field))"
                        [style.--ui-badge-bg]="badgeBgFor(detailTextOf(selectedRow(), field))"
                        [style.--ui-badge-text]="badgeTextFor(detailTextOf(selectedRow(), field))"
                      >
                        {{ detailTextOf(selectedRow(), field) | uiLabelTranslate }}
                      </span>
                    } @else if (field.type === 'stock') {
                      <span
                        class="ui-table__badge"
                        [attr.data-tone]="badgeToneFor(detailTextOf(selectedRow(), field))"
                        [style.--ui-badge-bg]="badgeBgFor(detailTextOf(selectedRow(), field))"
                        [style.--ui-badge-text]="badgeTextFor(detailTextOf(selectedRow(), field))"
                      >
                        {{ detailTextOf(selectedRow(), field) | uiLabelTranslate }}
                      </span>
                    } @else if (field.type === 'resource-summary') {
                      <ui-resource-summary [items]="resourceSummaryItemsOf(selectedRow(), { key: field.key, label: field.label, type: 'resource-summary' })"></ui-resource-summary>
                    } @else if (field.type === 'table') {
                      <div class="ui-table-detail-field--table">
                        @let tableRows = getDetailTableRows(selectedRow(), field);
                        <!-- Conditional branch documents the visible UI state for this condition. -->
                        @if (tableRows.length === 0) {
                          <span class="ui-table-detail-field__empty">{{ 'No records found.' | uiLabelTranslate }}</span>
                        } @else {
                          <div class="ui-table-detail-inline-table">
                            <table class="ui-table-detail-inline-table__table">
                              <thead>
                                <tr>
                                  <!-- Repeated block documents how collection data renders in the UI. -->
                                  @for (col of field.tableColumns ?? []; track col.key) {
                                    <th
                                      class="ui-table-detail-inline-table__th"
                                      [style.width]="col.width || null"
                                      [style.min-width]="col.minWidth || null"
                                      [style.max-width]="col.maxWidth || null"
                                      [style.white-space]="col.whiteSpace || null"
                                    >{{ col.label | uiLabelTranslate }}</th>
                                  }
                                </tr>
                              </thead>
                              <tbody>
                                <!-- Repeated block documents how collection data renders in the UI. -->
                                @for (tRow of tableRows; track $index) {
                                  <tr>
                                    <!-- Repeated block documents how collection data renders in the UI. -->
                                    @for (col of field.tableColumns ?? []; track col.key) {
                                      @let cellVal = textOf(tRow, col);
                                      <td
                                        class="ui-table-detail-inline-table__td"
                                        [class.ui-table-detail-inline-table__td--wrap]="col.wrap"
                                        [style.min-width]="col.minWidth || null"
                                        [style.max-width]="col.maxWidth || null"
                                        [style.white-space]="col.whiteSpace || null"
                                      >
                                        <!-- Conditional branch documents the visible UI state for this condition. -->
                                        @if (col.type === 'status' || col.type === 'stock') {
                                          <span
                                            class="ui-table__badge"
                                            [attr.data-tone]="detailTableBadgeToneFor(cellVal, field)"
                                          >{{ cellVal | uiLabelTranslate }}</span>
                                        } @else if (col.type === 'resource-summary') {
                                          <ui-resource-summary [items]="resourceSummaryItemsOf(tRow, col)"></ui-resource-summary>
                                        } @else if (col.type === 'link') {
                                          <!-- Conditional branch documents the visible UI state for this condition. -->
                                          @if (cellVal) {
                                            <!-- Conditional branch documents the visible UI state for this condition. -->
                                            @if (col.linkAction) {
                                              <button
                                                type="button"
                                                class="ui-table-detail-inline-table__link-btn"
                                                [attr.aria-label]="cellVal"
                                                (click)="col.linkAction(tRow)"
                                              >
                                                {{ cellVal | uiLabelTranslate }}
                                              </button>
                                            } @else {
                                              <a
                                                class="ui-table-detail-inline-table__link-btn"
                                                [href]="cellVal"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                [attr.aria-label]="('shared.buttons.preview' | uiLabelTranslate) + ' ' + (col.label | uiLabelTranslate)"
                                              >
                                                <span class="material-symbols-outlined">visibility</span>
                                              </a>
                                            }
                                          } @else {
                                            <span class="ui-table-detail-inline-table__empty-cell">—</span>
                                          }
                                        } @else {
                                          {{ cellVal | uiLabelTranslate }}
                                        }
                                      </td>
                                    }
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        }
                      </div>
                    } @else if (field.key === 'documents') {
                      <div class="ui-table-detail-documents">
                        @let docs = getDocuments(selectedRow());
                        <!-- Conditional branch documents the visible UI state for this condition. -->
                        @if (docs.length === 0) {
                          <span class="ui-table-detail-documents__empty">{{ 'shared.documents.noDocumentsUploaded' | uiLabelTranslate }}</span>
                        } @else {
                          <!-- Repeated block documents how collection data renders in the UI. -->
                          @for (doc of docs; track doc.id) {
                            <div class="ui-table-detail-documents__item">
                              <span class="ui-table-detail-documents__item-icon material-symbols-rounded">
                                {{ doc.type === 'image' ? 'image' : 'description' }}
                              </span>
                              <span class="ui-table-detail-documents__item-name">{{ doc.name }}</span>
                              <div class="ui-table-detail-documents__actions">
                                <button
                                  type="button"
                                  class="ui-table-detail-documents__preview-btn"
                                  [attr.aria-label]="('shared.buttons.preview' | uiLabelTranslate) + ' ' + doc.name"
                                  [attr.title]="('shared.buttons.preview' | uiLabelTranslate) + ' ' + doc.name"
                                  (click)="viewDocuments.emit(doc)"
                                >
                                  <span class="material-symbols-rounded" aria-hidden="true">visibility</span>
                                </button>
                                <!-- Conditional branch documents the visible UI state for this condition. -->
                                @if (doc.downloadUrl) {
                                  <a
                                    class="ui-table-detail-documents__download-btn"
                                    [href]="doc.downloadUrl"
                                    [download]="doc.name"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    [attr.aria-label]="('shared.buttons.download' | uiLabelTranslate) + ' ' + doc.name"
                                    [attr.title]="('shared.buttons.download' | uiLabelTranslate) + ' ' + doc.name"
                                  >
                                    <span class="material-symbols-rounded" aria-hidden="true">download</span>
                                  </a>
                                }
                              </div>
                            </div>
                          }
                        }
                      </div>
                    } @else {
                      <span class="ui-table-detail-field__value">{{ detailTextOf(selectedRow(), field) | uiLabelTranslate }}</span>
                    }
                  </div>
                }
              </div>
            </article>
          }
        } @else {
          <!-- Drawer/detail section for record inspection. -->
          <p class="ui-table-drawer__empty">{{ (tab.emptyMessage || 'shared.table.noDetailContent') | uiLabelTranslate }}</p>
        }
      </section>
    }
  <!-- Drawer/detail section for record inspection. -->
  </p-drawer>
</section>
